import { useMemo, useRef, useState } from 'react'
import { HELP_DEFINITION_COUNT, HELP_SECTIONS, type HelpDefinition } from '../help/content'

/**
 * In-app documentation with sidebar navigation and search.
 *
 * Available to every authenticated user, not gated to admins: a viewer needs to
 * know what "degraded" or "p90" means at least as much as an admin does, and the
 * content describes behaviour rather than exposing any configuration or data.
 */
export function Help() {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(HELP_SECTIONS[0].id)
  const bodyRef = useRef<HTMLDivElement>(null)

  const needle = query.trim().toLowerCase()

  /**
   * Search matches individual DEFINITIONS, not whole sections — that is the
   * advantage of holding the content as data. Typing "p90" returns the one row
   * that defines it, labelled with the section it belongs to, rather than
   * scrolling you to a page and leaving you to scan.
   */
  const results = useMemo(() => {
    if (!needle) return null
    const out: { section: string; sectionId: string; def: HelpDefinition }[] = []
    for (const section of HELP_SECTIONS) {
      for (const def of section.definitions ?? []) {
        const haystack = `${def.term} ${def.description} ${def.note ?? ''}`.toLowerCase()
        if (haystack.includes(needle)) {
          out.push({ section: section.title, sectionId: section.id, def })
        }
      }
      // Intro prose is searched too, surfaced as a section-level hit so a concept
      // explained only in prose ("combined window") is still findable.
      for (const para of section.intro ?? []) {
        if (para.toLowerCase().includes(needle)) {
          out.push({
            section: section.title,
            sectionId: section.id,
            def: { term: section.title, description: para },
          })
          break
        }
      }
    }
    return out
  }, [needle])

  const go = (id: string) => {
    setQuery('')
    setActive(id)
    bodyRef.current?.scrollTo({ top: 0 })
  }

  const section = HELP_SECTIONS.find(s => s.id === active) ?? HELP_SECTIONS[0]

  const highlight = (text: string) => {
    if (!needle) return text
    const idx = text.toLowerCase().indexOf(needle)
    if (idx < 0) return text
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: '#5a4a00', color: '#ffe082', padding: '0 2px' }}>
          {text.slice(idx, idx + needle.length)}
        </mark>
        {text.slice(idx + needle.length)}
      </>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Help</h2>
        <span style={{ fontSize: '0.75rem', color: '#9aa0a6' }}>
          {HELP_DEFINITION_COUNT} terms defined
        </span>
      </div>

      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search every metric, field and status…"
        aria-label="Search help"
        style={{
          width: '100%', maxWidth: 520, padding: '0.55rem 0.75rem', marginBottom: '1rem',
          background: '#1e1e1e', color: '#f5f5f5', border: '1px solid #333',
          borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box',
        }}
      />

      {results ? (
        <div>
          <p style={{ fontSize: '0.82rem', color: '#9aa0a6', margin: '0 0 0.8rem' }}>
            {results.length === 0
              ? `Nothing matches "${query}".`
              : `${results.length} match${results.length === 1 ? '' : 'es'} for "${query}"`}
          </p>
          {results.map((r, i) => (
            <div key={i} style={defBoxStyle}>
              <button onClick={() => go(r.sectionId)} style={sectionPillStyle}>
                {r.section}
              </button>
              <div style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.86rem', margin: '0.4rem 0 0.25rem' }}>
                {highlight(r.def.term)}
              </div>
              <div style={{ fontSize: '0.86rem', lineHeight: 1.6, color: '#d5d5d5' }}>
                {highlight(r.def.description)}
              </div>
              {r.def.note && (
                <div style={noteStyle}>{highlight(r.def.note)}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Sidebar */}
          <nav style={{ minWidth: 210, flexShrink: 0 }} aria-label="Help sections">
            {HELP_SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => go(s.id)}
                aria-current={s.id === active ? 'page' : undefined}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '0.5rem 0.7rem', marginBottom: '0.2rem',
                  background: s.id === active ? '#2a2a2a' : 'transparent',
                  color: s.id === active ? '#fff' : '#c5c5c5',
                  border: 'none', borderLeft: `3px solid ${s.id === active ? '#FF4133' : 'transparent'}`,
                  borderRadius: '0 4px 4px 0', cursor: 'pointer', fontSize: '0.84rem',
                  fontWeight: s.id === active ? 600 : 400,
                }}
              >
                {s.title}
              </button>
            ))}
          </nav>

          {/* Body */}
          <div ref={bodyRef} style={{ flex: 1, minWidth: 320, maxWidth: 820 }}>
            <h3 style={{ marginTop: 0, fontSize: '1.05rem' }}>{section.title}</h3>

            {section.intro?.map((p, i) => (
              <p key={i} style={{ fontSize: '0.88rem', lineHeight: 1.7, color: '#d5d5d5', marginTop: 0 }}>
                {p}
              </p>
            ))}

            {section.definitions && section.definitions.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                {section.definitions.map(d => (
                  <div key={d.term} style={defBoxStyle}>
                    <div style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.86rem', marginBottom: '0.25rem' }}>
                      {d.term}
                    </div>
                    <div style={{ fontSize: '0.86rem', lineHeight: 1.6, color: '#d5d5d5' }}>
                      {d.description}
                    </div>
                    {d.note && <div style={noteStyle}>{d.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const defBoxStyle: React.CSSProperties = {
  background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 6,
  padding: '0.7rem 0.85rem', marginBottom: '0.6rem',
}

const noteStyle: React.CSSProperties = {
  marginTop: '0.45rem', paddingLeft: '0.6rem', borderLeft: '2px solid #4a3a00',
  fontSize: '0.8rem', lineHeight: 1.55, color: '#c9b678',
}

const sectionPillStyle: React.CSSProperties = {
  background: '#262626', color: '#9aa0a6', border: '1px solid #333',
  borderRadius: 10, padding: '0.1rem 0.5rem', fontSize: '0.68rem', cursor: 'pointer',
}

export default Help
